import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Spinner,
  Stack,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { RiCloseCircleFill } from "react-icons/ri";
import TypeaheadWidget from "../TypeaheadWidget";
import { getBulkEditConfig as getLibrariesBulkEditConfig, bulkEditLibraries } from "../../services/Libraries";
import { getAllDocTypesData } from "../../services/DocumentTypes";
import { getMasterListAliasSuggestions } from "../../services/masterlistAlias";
import { getResearchCategoriesAPI } from "../../services/Films";
import { showErrorToast, showSuccessToast, showWarningToast } from "../../util/errorHandler";

// Reuses the same options APIs the single-record Library edit form already
// uses for these fields (Section1.js's fetchDocTypes/fetchMasterlistAliasSuggestions)
// rather than introducing a second way to search the same data.
const fetchDocTypeOptions = async (inputValue) => {
  try {
    const result = await getAllDocTypesData();
    const list = result?.data?.data ?? result?.data;
    const items = Array.isArray(list) ? list : [];
    return items
      .map((item) => ({ label: item.alias || "", value: item.alias || "" }))
      .filter((opt) => opt.label.toLowerCase().includes((inputValue || "").toLowerCase()));
  } catch {
    return [];
  }
};

const fetchMasterlistAliasOptions = async (inputValue) => {
  if (!inputValue) return [];
  try {
    const result = await getMasterListAliasSuggestions(20, 1, inputValue);
    const list = result?.data?.data || [];
    return list.map((item) => ({ label: item.alias, value: item.alias }));
  } catch {
    return [];
  }
};

// getResearchCategoriesAPI returns the full list in one call (no server-side
// search) — same shape/transform as ResearchCategories.js's "typeahead"
// widget (its default keyForLabelValue="alias"; label/value both keyed by
// each entry's "alias" field, NOT "value" — "value" is a different property
// used only by that form's separate research_categoryN typeahead, which
// bulk edit doesn't expose). Both Library's and Archive's 4 independent
// research_category{1-4}_alias bulk-edit fields write alias values (see the
// backend's libraryBulkEdit/fieldConfig.js and archiveBulkEdit/fieldConfig.js),
// so the alias is what must be collected here regardless of which collection
// is being edited or which of the 4 slots is being set. Fetched once and
// filtered client-side per keystroke instead of re-fetching.
let cachedResearchCategoryOptions = null;
const fetchResearchCategoryOptions = async (inputValue) => {
  try {
    if (!cachedResearchCategoryOptions) {
      const result = await getResearchCategoriesAPI();
      cachedResearchCategoryOptions = (result || []).map((itemArray) => {
        const entity = (itemArray || []).reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {});
        return { label: entity.alias, value: entity.alias };
      });
    }
    return cachedResearchCategoryOptions.filter((opt) =>
      (opt.label || "").toLowerCase().includes((inputValue || "").toLowerCase()),
    );
  } catch {
    return [];
  }
};

// Matches the backend's own check (services/bulkEditShared/createBulkEditService.js,
// fieldConfig.validation === "slug") — a keyword alias must be a single word
// or hyphen-joined words (e.g. "amitabh-bachchan"), never a space-separated
// phrase. Checked here too so the admin sees the error before submitting,
// not only after the backend rejects it.
const SLUG_PATTERN = /^[A-Za-z0-9-]+$/;
function slugValidationError(field, value) {
  if (field.validation !== "slug" || !value) return null;
  return SLUG_PATTERN.test(value.trim())
    ? null
    : 'Only letters, numbers, and hyphens are allowed — no spaces (e.g. "amitabh-bachchan").';
}

// Client-side hint only, computed from whatever records are currently
// loaded/rendered — an allFiltered selection may include far more records
// than are on screen, so this never claims to be exhaustive (per the
// performance requirement: never fetch the full selection to the browser
// just to compute this).
function mixedValueHint(records, key) {
  if (!records || records.length === 0) return null;
  const values = records.map((r) => r?.[key]);
  const distinct = Array.from(new Set(values.map((v) => JSON.stringify(v ?? null))));
  if (distinct.length > 1) return "Mixed";
  const only = values[0];
  if (only === undefined || only === null || only === "") return null;
  return Array.isArray(only) ? only.join(", ") : String(only);
}

// One field's (optional) enable checkbox + its type-specific control.
// Extracted so the same row renders identically whether it's a standalone
// field or inside one of the Keywords/Research Category accordion sections.
//
// `showCheckbox` is false for Keywords/Research Category fields: with 14+4
// independent fields, requiring a check-then-fill-in click for each one was
// pure friction for a set-only field — typing a value (or picking one) is
// itself the "include this in the change" signal, and clearing it back to
// empty removes it from the change again. `isVisible` (the only standalone
// field today) keeps the checkbox since "no value yet" and "false" are both
// meaningful, valid states for a boolean, so presence-of-value can't stand
// in for intent the way it can for a not-yet-set text/relation field.
function FieldRow({ field, enabled, values, selectedRecordsLoaded, toggleField, setFieldValue, showCheckbox = true }) {
  const current = values[field.key];
  const hint = mixedValueHint(selectedRecordsLoaded, field.key);
  const hasValue = !!current?.value;
  const validationError = slugValidationError(field, current?.value);
  const isEnabled = showCheckbox ? !!enabled[field.key] : true;
  // Checkbox fields: hide the hint the moment the field is checked, and keep
  // it hidden regardless of what value gets chosen afterward (checking
  // isVisible then flipping its Switch to false shouldn't make the hint
  // reappear — false is a deliberate, valid value, not "no value yet").
  // Checkbox-less (grouped) fields have no "checked" state to key off of, so
  // they fall back to value-presence instead: hidden once something's typed
  // or picked, shown again if cleared back to empty.
  const showHint = hint && (showCheckbox ? !isEnabled : !hasValue);

  return (
    <Box borderBottom="1px solid" borderColor="gray.100" pb={3}>
      {showCheckbox ? (
        <Checkbox isChecked={isEnabled} onChange={(e) => toggleField(field, e.target.checked)}>
          <Text fontWeight="600" fontSize="14px">
            {field.label}
          </Text>
        </Checkbox>
      ) : (
        <Text fontWeight="600" fontSize="14px">
          {field.label}
        </Text>
      )}
      {showHint && (
        <Text fontSize="12px" color="gray.400" ml={showCheckbox ? 6 : 0}>
          Current: {hint}
        </Text>
      )}
      {isEnabled && (
        <Box ml={showCheckbox ? 6 : 0} mt={2}>
          {(field.type === "text" || field.type === "select") && (
            <>
              <Input
                size="sm"
                value={current?.value || ""}
                isInvalid={!!validationError}
                onChange={(e) => setFieldValue(field.key, { value: e.target.value })}
              />
              {validationError && (
                <Text fontSize="12px" color="red.500" mt={1}>
                  {validationError}
                </Text>
              )}
            </>
          )}
          {field.type === "boolean" && (
            <Switch
              isChecked={!!current?.value}
              onChange={(e) => setFieldValue(field.key, { value: e.target.checked })}
            />
          )}
          {field.type === "relation" && field.optionsSource === "document_types" && (
            <TypeaheadWidget
              id={`bulk-${field.key}`}
              value={current?.value || ""}
              onChange={(v) => setFieldValue(field.key, { value: v })}
              fetchOptions={fetchDocTypeOptions}
            />
          )}
          {field.type === "relation" && field.optionsSource === "masterlist" && (
            <TypeaheadWidget
              id={`bulk-${field.key}`}
              value={current?.value || ""}
              onChange={(v) => setFieldValue(field.key, { value: v })}
              fetchOptions={fetchMasterlistAliasOptions}
            />
          )}
          {field.type === "relation" && field.optionsSource === "personality" && (
            <Input
              size="sm"
              value={current?.value || ""}
              onChange={(e) => setFieldValue(field.key, { value: e.target.value })}
              placeholder="Personality alias"
            />
          )}
          {field.type === "relation" && field.optionsSource === "research_categories" && (
            <TypeaheadWidget
              id={`bulk-${field.key}`}
              value={current?.value || ""}
              onChange={(v) => setFieldValue(field.key, { value: v })}
              fetchOptions={fetchResearchCategoryOptions}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function defaultChangeForField(field) {
  if (field.type === "boolean") {
    return { operation: "set", value: true };
  }
  return { operation: "set", value: "" };
}

// `getConfig`/`submit` default to Library's own API functions so every
// existing caller (LibrariesTable.js) keeps working unchanged; other entities
// (e.g. Archive) pass their own getBulkEditConfig/bulkEdit functions, which
// must match the same shape: getConfig() -> Promise<field[]>, submit(payload)
// -> Promise<{success, updatedCount, failedCount, ...}>.
const BulkEditPanel = ({
  selection,
  filterOptions,
  records,
  onClose,
  onApplied,
  getConfig = getLibrariesBulkEditConfig,
  submit = bulkEditLibraries,
}) => {
  const toast = useToast();
  const [fields, setFields] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelRef = useRef();
  const requestIdRef = useRef(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    let alive = true;
    getConfig()
      .then((cfg) => {
        if (!alive) return;
        setFields([...cfg].sort((a, b) => (a.order || 0) - (b.order || 0)));
      })
      .catch(() => {
        if (alive) showErrorToast(toast, "Failed to load bulk-edit fields");
      })
      .finally(() => {
        if (alive) setLoadingConfig(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleField = (field, checked) => {
    setEnabled((prev) => ({ ...prev, [field.key]: checked }));
    if (checked) {
      setValues((prev) => ({ ...prev, [field.key]: prev[field.key] || defaultChangeForField(field) }));
    }
  };

  // Falls back to this field's default change shape (right `operation`
  // included) when it has no pending value yet — needed for
  // checkbox-less (grouped) fields, whose first-ever change comes directly
  // from the input's onChange rather than from toggleField's own seeding.
  const setFieldValue = (key, patch) => {
    setValues((prev) => {
      const field = fields.find((f) => f.key === key);
      const base = prev[key] || (field ? defaultChangeForField(field) : {});
      return { ...prev, [key]: { ...base, ...patch } };
    });
  };

  // A field counts toward the pending change set differently depending on
  // whether it has an enable checkbox: standalone fields (isVisible) are
  // "active" purely by checkbox state, since a boolean's value alone can't
  // distinguish "not touched" from "set to false". Grouped fields
  // (Keywords/Research Category) have no checkbox, so they're "active"
  // exactly when they hold a non-empty value.
  const isFieldActive = (field) => {
    if (!field.group) return !!enabled[field.key];
    const current = values[field.key];
    return !!current?.value;
  };

  const activeFields = fields.filter(isFieldActive);
  const activeKeys = activeFields.map((f) => f.key);
  const hasInvalidActiveField = activeFields.some((f) => !!slugValidationError(f, values[f.key]?.value));

  const buildChanges = () => {
    const changes = {};
    activeKeys.forEach((key) => {
      const current = values[key];
      if (!current) return;
      changes[key] = current;
    });
    return changes;
  };

  const openConfirm = () => {
    if (activeKeys.length === 0 || selection.count === 0 || hasInvalidActiveField) return;
    if (!requestIdRef.current) {
      requestIdRef.current =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    setConfirmOpen(true);
  };

  const handleApply = async () => {
    // Ref-guarded (not just `submitting` state) so a fast double-click before
    // the next render can't fire a second in-flight request.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        requestId: requestIdRef.current,
        selection:
          selection.mode === "allFiltered"
            ? { type: "filter", filters: filterOptions, excludedIds: selection.excludedIds }
            : { type: "ids", ids: selection.selectedIds },
        changes: buildChanges(),
      };
      const result = await submit(payload);
      if (!result?.success) {
        showErrorToast(toast, result?.message || "Bulk update failed");
        return;
      }
      if (result.failedCount > 0) {
        showWarningToast(toast, `${result.updatedCount} updated, ${result.failedCount} failed`);
      } else {
        showSuccessToast(toast, `${result.updatedCount} record(s) updated successfully`);
      }
      setConfirmOpen(false);
      requestIdRef.current = null;
      // The panel and the selection stay open on success (the parent no
      // longer clears/closes them) — reset the form itself so it doesn't
      // keep showing the just-applied fields/values as if still pending.
      setEnabled({});
      setValues({});
      onApplied && onApplied(result);
    } catch (error) {
      showErrorToast(
        toast,
        error?.response?.data?.message || error?.message || "Unable to complete bulk update",
      );
      // Do not close the panel or clear the selection on failure — the admin
      // should be able to see what happened and retry.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const selectedRecordsLoaded = useMemo(
    () => (records || []).filter((r) => selection.isSelected(r?._id)),
    [records, selection],
  );

  // Fields with no declared group render directly; fields sharing a group
  // (currently "Keywords" and "Research Category") collapse into one
  // accordion section each, in the order their group first appears.
  const standaloneFields = [];
  const groupOrder = [];
  const groupedFields = {};
  fields.forEach((field) => {
    if (!field.group) {
      standaloneFields.push(field);
      return;
    }
    if (!groupedFields[field.group]) {
      groupedFields[field.group] = [];
      groupOrder.push(field.group);
    }
    groupedFields[field.group].push(field);
  });

  const fieldRowProps = { enabled, values, selectedRecordsLoaded, toggleField, setFieldValue };

  return (
    <Box
      width="280px"
      borderRadius="md"
      boxShadow="sm"
      bg="white"
      border="0.8px solid"
      borderColor="#dcdcdc"
      position="sticky"
      top="0px"
    >
      <HStack pt="5px" pr="5px" justifyContent="flex-end">
        <RiCloseCircleFill size="22px" cursor="pointer" onClick={onClose} />
      </HStack>
      <Stack px={4} pb={4} spacing={3}>
        <Text fontWeight="700" fontSize="15px">
          Bulk Edit
        </Text>
        <Text fontSize="13px" color="gray.500">
          {selection.count} record{selection.count === 1 ? "" : "s"} selected
        </Text>

        {loadingConfig ? (
          <Spinner size="sm" />
        ) : (
          <Stack spacing={4} maxH="50vh" overflowY="auto">
            {standaloneFields.map((field) => (
              <FieldRow key={field.key} field={field} {...fieldRowProps} />
            ))}
            {groupOrder.length > 0 && (
              <Accordion allowMultiple>
                {groupOrder.map((groupName) => {
                  const groupFields = groupedFields[groupName];
                  const enabledCount = groupFields.filter(isFieldActive).length;
                  // Both the Keywords and Research Category groups are
                  // set-only (no add/remove) — applying a value always
                  // replaces whatever's already there per record, so call
                  // that out on the group header rather than repeating it on
                  // every individual field.
                  const isOverwriteGroup = groupFields.every(
                    (f) => f.operations.length === 1 && f.operations[0] === "set",
                  );
                  return (
                    <AccordionItem key={groupName} border="none">
                      <AccordionButton px={0} _hover={{ bg: "transparent" }}>
                        <HStack flex="1" justifyContent="space-between">
                          <Text fontWeight="600" fontSize="14px">
                            {groupName}
                            {isOverwriteGroup && (
                              <Text as="span" fontWeight="400" color="orange.600">
                                {" "}
                                (Overwrite)
                              </Text>
                            )}
                          </Text>
                          <HStack spacing={2}>
                            {enabledCount > 0 && (
                              <Badge colorScheme="blue" borderRadius="full">
                                {enabledCount}
                              </Badge>
                            )}
                            <AccordionIcon />
                          </HStack>
                        </HStack>
                      </AccordionButton>
                      <AccordionPanel px={0}>
                        <Stack spacing={4}>
                          {groupFields.map((field) => (
                            <FieldRow key={field.key} field={field} {...fieldRowProps} showCheckbox={false} />
                          ))}
                        </Stack>
                      </AccordionPanel>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </Stack>
        )}

        <Button
          colorScheme="blue"
          isDisabled={activeKeys.length === 0 || selection.count === 0 || hasInvalidActiveField}
          onClick={openConfirm}
        >
          Apply Changes
        </Button>
      </Stack>

      <AlertDialog
        isOpen={confirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => !submitting && setConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Apply changes to {selection.count} record(s)?</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={1}>
                {activeKeys.map((key) => {
                  const field = fields.find((f) => f.key === key);
                  const v = values[key];
                  const desc = String(v?.value);
                  return (
                    <Text key={key} fontSize="14px">
                      {field?.label}: {desc}
                    </Text>
                  );
                })}
                {selection.mode === "allFiltered" && (
                  <Text fontSize="13px" color="orange.600" pt={2}>
                    This applies to all records matching the current filter, not just the ones on
                    screen.
                  </Text>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmOpen(false)} isDisabled={submitting}>
                Cancel
              </Button>
              <Button colorScheme="blue" ml={3} onClick={handleApply} isLoading={submitting}>
                Confirm Changes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default BulkEditPanel;
